/**
 * How a breadcrumb names an entity, as pure functions.
 *
 * These live outside `breadcrumbs.tsx` so the rules can be tested without
 * mounting a router and a query client. Each one answers the same question —
 * "what would a person call this thing?" — and each one has a fallback, because
 * the alternative is the failure these exist to prevent:
 *
 *   AGENTS.md guardrail: **no raw UUID is ever shown to a human.**
 *
 * A truncated id is still the last resort, but it is a last resort, not a
 * default. `/assets/:uuid` had no resolver at all before #1011, so a device
 * page's trail read `#c65f2671` where a serial number belongs.
 */

/** Shortened id — the last resort when a record has no human name at all. */
export function shortId(id: string): string {
    return `#${id.slice(0, 8)}`;
}

/**
 * What to call a device.
 *
 * Serial first: it is the number stamped on the assembly, and the one the
 * customer, the city and the tech all use. The device's own name ("RP-1") is
 * an internal label and comes second — useful, but not what someone reads off
 * a test report.
 */
export function assetCrumbLabel(
    asset: { serial_number?: string | null; name?: string | null } | null | undefined,
    id: string,
): string {
    return asset?.serial_number?.trim() || asset?.name?.trim() || shortId(id);
}
